/**
 * i18n SEO configuration
 *
 * Central source of truth for which language versions of iSupplyAI exist and
 * where they live. When you ship a Spanish, French, or German translation:
 *   1. Add the locale code below (e.g. 'es').
 *   2. Set `path_prefix` to the URL prefix the localized site is served from
 *      (e.g. '/es' for path-based) OR `host` for a subdomain (e.g. 'es.isupplyai.com').
 *   3. Hreflang tags will be emitted automatically by:
 *        • `HreflangTags.jsx` (client-side, for crawlers that run JS)
 *        • `setupProxy.js` injectMeta()  (server-side, for first crawl)
 *
 * The default locale is 'en' served at the bare `SITE_URL`.
 *
 * NOTE: This file is consumed by both ES modules (React) and CommonJS
 * (setupProxy.js). It MUST stay portable — no JSX, no React imports.
 */

const SITE_URL = 'https://isupplyai.com';

// Locales currently shipped. Add new entries when translations launch.
const LOCALES = [
  {
    code: 'en',
    hreflang: 'en',
    // For locales served at the bare host with no prefix
    path_prefix: '',
    host: SITE_URL,
    is_default: true, // emits hreflang="x-default" alongside hreflang="en"
  },
  // Example future entries — uncomment when translations ship:
  // { code: 'es', hreflang: 'es', path_prefix: '/es', host: SITE_URL, is_default: false },
  // { code: 'fr', hreflang: 'fr', path_prefix: '/fr', host: SITE_URL, is_default: false },
  // { code: 'de', hreflang: 'de', path_prefix: '/de', host: SITE_URL, is_default: false },
];

/**
 * Given a canonical English path (e.g. '/blog/foo'), return the full URL list
 * of hreflang alternates Google expects:
 *   [{ hreflang: 'en', href: 'https://isupplyai.com/blog/foo' },
 *    { hreflang: 'x-default', href: 'https://isupplyai.com/blog/foo' }]
 *
 * As locales are added in LOCALES above, this automatically expands.
 */
function buildHreflangAlternates(canonicalPath) {
  // Normalize: strip any existing locale prefix to get the "bare" path
  let bare = canonicalPath || '/';
  for (const loc of LOCALES) {
    if (loc.path_prefix && bare.startsWith(loc.path_prefix + '/')) {
      bare = bare.slice(loc.path_prefix.length);
      break;
    }
    if (loc.path_prefix && bare === loc.path_prefix) {
      bare = '/';
      break;
    }
  }

  const alternates = [];
  for (const loc of LOCALES) {
    const href = `${loc.host}${loc.path_prefix}${bare}`.replace(/\/+$/, bare === '/' ? '/' : '');
    alternates.push({ hreflang: loc.hreflang, href });
    if (loc.is_default) {
      alternates.push({ hreflang: 'x-default', href });
    }
  }
  return alternates;
}

module.exports = {
  SITE_URL,
  LOCALES,
  buildHreflangAlternates,
};
